/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  createCorrection,
  removeOrder,
  editOrderIssueDate,
  getOrderDocuments,
  getOrderIssueDate,
  getOrderItemAttributes,
  getOrderItemAttributesToEdit,
  getOrderItemHistory,
  getOrderItemsTable,
  getOrderPayments,
  getOrdersTable,
  postSaveOrderItemAttributes,
  setOrderItemCashierOwner,
  removeOrderDocuments,
  setErrorSendAsSended,
  getInformationAboutCorrectedItems,
  getExportSettings,
  exportOrder,
  disableAcceptationReminder,
  getPurchaseNetPrice,
  editPurchaseNetPrice,
  createStandingOrder,
} from "@/requests/order/order";
import { OrderListItem } from "@/models/api/orders/OrderListItem";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { DataOptions } from "vuetify/types";
import OrderItemsListItem from "@/models/api/orders/OrderItemsListItem";
import OrderItemHistoryListItem from "@/models/api/orders/OrderItemHistoryListItem";
import OrderPaymentsListItem from "@/models/api/orders/OrderPaymentsListItem";
import { OrderDocumentResponse } from "@/models/api/orders/OrderDocumentListItem";
import { FILTER_NAMES } from "@/static/filterNames";
import OrderOwnersModule from "@/store/modules/orders/orderOwners";
import { getCalculatedValue, setTemporaryOnOrderInOrderTable } from "@/requests/order/orderAddEdit";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import OrderCommentModule from "@/store/modules/orders/orderComment";
import { OrderCreateCorrection } from "@/models/api/orders/OrderCreateCorrection";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import OrderCreateDocumentModule from "@/store/modules/orders/orderCreateDocument";
import { AttributeItem } from "@/models/api/bookings/BookingGetTemporaryItem";
import { generateXmlDocument } from "@/requests/document";
import { getOpenTemporaryFile } from "@/requests/general";
import { OrderItemReceipt } from "@/models/api/orders/DocumentForReceipt";
import OrderItemPurchaseNetPrice from "@/models/api/orders/OrderItemPurchaseNetPrice";
import { CALCULATE_ORDER_ITEM_VALUE } from "@/static/calculateOrderItemValueTypes";
import {
  OrderGetTemporaryCalculateValueRequest,
  OrderGetTemporaryCalculateValueResponse,
} from "@/models/api/orders/temporary/OrderGetTemporaryCalculateValue";
import { getExchangeRateLastAvailableByString } from "@/requests/exchangeRate";
import { ExchangeRate } from "@/models/api/ExchangeRate";
import { StandingOrder } from "@/models/api/administration/contractors/ContractorStandingOrder";
import OrderMergeModule from "@/store/modules/orders/orderMerge";
import OrderExportSetting from "@/models/api/orders/OrderExportSetting";
import { CustomDataTableHeader } from "@/models/components/dataTables/CustomDataTableHeader";

export interface OrdersStateInterface {
  isOrdersLoading: boolean;
  isOrderItemHistoryLoading: boolean;
  orders: Array<OrderListItem>;
  ordersFilters: Array<CriterionBase>;
  ordersDataTableOptions: DataOptions;
  ordersCount: number;
  selectedOrderId: null | string;
  isOrderItemsLoading: boolean;
  orderItems: Array<OrderItemsListItem>;
  orderItemsFilters: Array<CriterionItemRequest>;
  orderItemsDataTableOptions: DataOptions;
  orderItemsCount: number;
  orderItemHistory: Array<OrderItemHistoryListItem>;
  selectedOrderItemId: null | string;
  isOrderItemAttributesLoading: boolean;
  orderItemAttributes: Array<AttributeItem>;
  orderItemAttributesToEdit: Array<AttributeItem>;
  isOrderPaymentsLoading: boolean;
  isOrderDocumentsLoading: boolean;
  orderPayments: Array<OrderPaymentsListItem>;
  orderDocuments: null | OrderDocumentResponse;
  orderFilters: null | Array<CriterionItemRequest>;
  issueDate: null | string;
  issueDateOrderId: null | string;
  fastCorrectionOrderId: null | string;
  standingOrderOrderId: null | string;
  isExportOrderDialog: boolean;
  isOrderItemPurchaseNetPriceDialog: boolean;
  orderExportSetting: OrderExportSetting;
  orderIdToShow: null | string;
  orderItemNameCriterion: null | CriterionItemRequest;
  returnProtocolOrderId: null | string;
  informationAboutCorrectedItemsOrderId: null | string;
  orderItemsReceipt: Array<OrderItemReceipt>;
  orderItemPurchaseNetPrice: OrderItemPurchaseNetPrice;
  editedPurchaseNetPriceOrderItemId: string | null;
  tableHeaders: CustomDataTableHeader[];
}

export const OrdersState: OrdersStateInterface = {
  orderFilters: null,
  isOrderDocumentsLoading: false,
  isOrderPaymentsLoading: false,
  orderDocuments: null,
  orderPayments: [],
  orderItemHistory: [],
  isOrderItemHistoryLoading: false,
  isOrdersLoading: false,
  orders: [],
  ordersFilters: [],
  ordersDataTableOptions: new DefaultDataOptions(),
  ordersCount: -1,
  selectedOrderId: null,
  isOrderItemsLoading: false,
  orderItems: [],
  orderItemsCount: -1,
  orderItemsDataTableOptions: new DefaultDataOptions(),
  orderItemsFilters: [],
  selectedOrderItemId: null,
  isOrderItemAttributesLoading: false,
  orderItemAttributes: [],
  orderItemAttributesToEdit: [],
  issueDate: null,
  issueDateOrderId: null,
  fastCorrectionOrderId: null,
  standingOrderOrderId: null,
  isExportOrderDialog: false,
  isOrderItemPurchaseNetPriceDialog: false,
  orderExportSetting: new OrderExportSetting(),
  orderIdToShow: null,
  orderItemNameCriterion: null,
  returnProtocolOrderId: null,
  orderItemsReceipt: [],
  orderItemPurchaseNetPrice: new OrderItemPurchaseNetPrice(),
  editedPurchaseNetPriceOrderItemId: null,
  informationAboutCorrectedItemsOrderId: null,
  tableHeaders: [],
};

const OrdersModule = defineModule({
  modules: {
    orderOwners: OrderOwnersModule,
    orderComment: OrderCommentModule,
    createDocument: OrderCreateDocumentModule,
    merge: OrderMergeModule,
  },
  state: OrdersState as OrdersStateInterface,
  namespaced: true as const,
  getters: {
    isOrderItemsLoading(...args): boolean {
      const { state } = _moduleGetterContext(args);
      return state.isOrderItemsLoading || state.isOrdersLoading;
    },
    isOrderDocumentsLoading(...args): boolean {
      const { state } = _moduleGetterContext(args);
      return state.isOrderDocumentsLoading || state.isOrdersLoading;
    },
    isOrderPaymentsLoading(...args): boolean {
      const { state } = _moduleGetterContext(args);
      return state.isOrderPaymentsLoading || state.isOrdersLoading;
    },
    isOrderItemAttributesLoading(...args): boolean {
      const { state, getters } = _moduleGetterContext(args);
      return state.isOrderItemAttributesLoading || getters.isOrderItemsLoading;
    },
    isOrderItemHistoryLoading(...args): boolean {
      const { state, getters } = _moduleGetterContext(args);
      return state.isOrderItemHistoryLoading || getters.isOrderItemsLoading;
    },
    selectedOrderItem(...args): OrderItemsListItem | null {
      const { state } = _moduleGetterContext(args);
      for (const rootOrderItem of state.orderItems) {
        if (rootOrderItem.id === state.selectedOrderItemId) return rootOrderItem;
        for (const childOrderItem of rootOrderItem.orderItems) {
          if (childOrderItem.id === state.selectedOrderItemId) return childOrderItem;
        }
      }
      return null;
    },
    selectedOrder(...args): OrderListItem | null {
      const { state } = _moduleGetterContext(args);
      for (const order of state.orders) {
        if (order.id === state.selectedOrderId) return order;
      }
      return null;
    },
  },
  mutations: {
    setDataTableOptions(state, param: DataOptions) {
      state.ordersDataTableOptions = JSON.parse(JSON.stringify(param));
    },
    setOrderItemsDataTableOptions(state, param: DataOptions) {
      state.orderItemsDataTableOptions = JSON.parse(JSON.stringify(param));
    },
    setOrders(state, param: Array<OrderListItem>) {
      state.orders = param;
      state.isOrdersLoading = false;
    },
    setOrderItems(state, param: Array<OrderItemsListItem>) {
      state.orderItems = param;
      state.isOrderItemsLoading = false;
    },
    setOrderItemAttributes(state, param: Array<AttributeItem>) {
      state.orderItemAttributes = param;
      state.isOrderItemAttributesLoading = false;
    },
    setOrderItemAttributesToEdit(state, param: Array<AttributeItem>) {
      state.orderItemAttributesToEdit = param;
    },
    setSelectedOrderId(state, param: string | null) {
      state.selectedOrderId = param;
    },
    setSelectedOrderItemId(state, param: string | null) {
      state.selectedOrderItemId = param;
    },
    startOrdersLoading(state) {
      state.isOrdersLoading = true;
    },
    startOrderItemsLoading(state) {
      state.isOrderItemsLoading = true;
    },
    startOrderItemAttributesLoading(state) {
      state.isOrderItemAttributesLoading = true;
    },
    setOrdersCount(state, param: number) {
      state.ordersCount = param;
    },
    setOrderItemsCount(state, param: number) {
      state.orderItemsCount = param;
    },
    setOrderItemHistory(state, param: Array<OrderItemHistoryListItem>) {
      state.orderItemHistory = param;
      state.isOrderItemHistoryLoading = false;
    },
    setOrderItemHistoryLoading(state) {
      state.isOrderItemHistoryLoading = true;
    },
    setOrderPaymentsLoading(state) {
      state.isOrderPaymentsLoading = true;
    },
    setOrderDocumentsLoading(state) {
      state.isOrderDocumentsLoading = true;
    },
    setOrderDocuments(state, param: OrderDocumentResponse) {
      state.orderDocuments = param;
      state.isOrderDocumentsLoading = false;
    },
    setOrderPayments(state, param: Array<OrderPaymentsListItem>) {
      state.orderPayments = param;
      state.isOrderPaymentsLoading = false;
    },
    setOrderFilters(state, param: Array<CriterionItemRequest>) {
      state.orderFilters = param;
    },
    setOrderItemFilters(state, param: Array<CriterionItemRequest>) {
      state.orderItemsFilters = param;
    },
    setIssueDate(state, param: null | string) {
      state.issueDate = param;
    },
    setIssueDateOrderId(state, param: null | string) {
      state.issueDateOrderId = param;
    },
    setFastCorrectionOrderId(state, param: null | string) {
      state.fastCorrectionOrderId = param;
    },
    setStandingOrderOrderId(state, param: null | string) {
      state.standingOrderOrderId = param;
    },
    setOrderExportSetting(state, param: OrderExportSetting) {
      state.orderExportSetting = param;
    },
    setIsExportOrderDialog(state, param: boolean) {
      state.isExportOrderDialog = param;
    },
    setIsOrderItemPurchaseNetPriceDialog(state, param: boolean) {
      state.isOrderItemPurchaseNetPriceDialog = param;
    },
    setOrderIdToShow(state, param: null | string) {
      state.orderIdToShow = param;
    },
    setOrderItemNameCriterion(state, param: null | CriterionItemRequest) {
      state.orderItemNameCriterion = param;
    },
    setReturnProtocolOrderId(state, param: null | string) {
      state.returnProtocolOrderId = param;
    },
    setOrderIdToInformationAboutCorrectedItems(state, param: null | string) {
      state.informationAboutCorrectedItemsOrderId = param;
    },
    setOrderItemsReceipt(state, param: Array<OrderItemReceipt>) {
      state.orderItemsReceipt = param;
    },
    setOrderItemPurchaseNetPrice(state, param: OrderItemPurchaseNetPrice) {
      state.orderItemPurchaseNetPrice = param;
    },
    setExchangeRateForPurchaseNetPrice(state, param: ExchangeRate) {
      state.orderItemPurchaseNetPrice.purchaseNetValueExchangeDate = param.exchangeDate;
      state.orderItemPurchaseNetPrice.purchaseNetValueExchangeRate = param.exchangeRateValue;
    },
    setCalculatedPurchaseNetPrice(state, param: OrderGetTemporaryCalculateValueResponse) {
      state.orderItemPurchaseNetPrice.purchaseNetValue = param.netValue;
      state.orderItemPurchaseNetPrice.purchaseNetValueInCurrency = param.netValueInCurrency;
    },
    setEditedPurchaseNetPriceOrderItemId(state, param: string) {
      state.editedPurchaseNetPriceOrderItemId = param;
    },
    setTableHeaders(state, param: CustomDataTableHeader[]) {
      state.tableHeaders = param;
    },
  },
  actions: {
    async getLastAvailableExchangeRate(
      context,
      param: {
        currency: string;
        exchangeDate: string;
      }
    ) {
      const { commit } = _moduleActionContext(context);
      const exchangeRate = await getExchangeRateLastAvailableByString(param.currency, param.exchangeDate);
      commit.setExchangeRateForPurchaseNetPrice(exchangeRate);
    },
    async calculateOrderItemValue(
      context,
      param: {
        value: number;
        exchangeRate: number;
        calculateType: CALCULATE_ORDER_ITEM_VALUE;
      }
    ) {
      const { commit } = _moduleActionContext(context);
      const calculatedValues = await getCalculatedValue(
        new OrderGetTemporaryCalculateValueRequest(1, param.value, "0", param.exchangeRate, param.calculateType)
      );
      commit.setCalculatedPurchaseNetPrice(calculatedValues);
    },
    async saveOrderItemPurchaseNet(context) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      await editPurchaseNetPrice(state.editedPurchaseNetPriceOrderItemId as string, state.orderItemPurchaseNetPrice);
      await dispatch.getOrderItemsTable();
    },
    async getOrdersTable(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.startOrdersLoading();
      commit.setSelectedOrderId(null);
      commit.setSelectedOrderItemId(null);
      commit.setOrderItems([]);
      const response = await getOrdersTable({
        count: state.ordersDataTableOptions.itemsPerPage,
        page: state.ordersDataTableOptions.page - 1,
        criterions: state.orderFilters,
        filterName: FILTER_NAMES.OrdersFilter,
      });
      commit.setOrders(response.items);
      commit.setOrdersCount(response.totalCount);
      if (response.items.length > 0) {
        commit.setSelectedOrderId(response.items[0].id);
      }
    },
    async getOrderItemsTable(context, skipSetDefault: boolean | undefined) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedOrderId !== null) {
        commit.startOrderItemsLoading();
        const response = await getOrderItemsTable(state.selectedOrderId, {
          criterions: state.orderItemsFilters,
          page: state.orderItemsDataTableOptions.page - 1,
          count: state.orderItemsDataTableOptions.itemsPerPage,
          filterName: FILTER_NAMES.OrderItemsInOrderFilter,
        });
        commit.setOrderItems(response.items);
        commit.setOrderItemsCount(response.totalCount);
        if (!skipSetDefault && response.items.length > 0) {
          commit.setSelectedOrderItemId(response.items[0].id);
        }
      }
    },
    async getOrderItemAttributesTable(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedOrderItemId !== null) {
        commit.startOrderItemAttributesLoading();
        const response = await getOrderItemAttributes(state.selectedOrderItemId);
        commit.setOrderItemAttributes(response);
      }
    },
    async getOrderItemAttributesToEdit(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedOrderItemId !== null) {
        const response = await getOrderItemAttributesToEdit(state.selectedOrderItemId);
        commit.setOrderItemAttributesToEdit(response);
      }
    },
    async saveOrderItemAttributes(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.selectedOrderItemId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await postSaveOrderItemAttributes(state.selectedOrderItemId, state.orderItemAttributesToEdit);
      }
    },
    async getOrderItemHistory(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedOrderItemId !== null) {
        commit.setOrderItemHistoryLoading();
        const response = await getOrderItemHistory(state.selectedOrderItemId);
        commit.setOrderItemHistory(response);
      }
    },
    async getOrderPaymentsTable(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedOrderId !== null) {
        commit.setOrderPaymentsLoading();
        const response = await getOrderPayments(state.selectedOrderId);
        commit.setOrderPayments(response);
      }
    },
    async getOrderDocumentsTable(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedOrderId !== null) {
        commit.setOrderDocumentsLoading();
        const response = await getOrderDocuments(state.selectedOrderId);
        commit.setOrderDocuments(response);
      }
    },
    async setTemporaryOnOrderInOrderTable(context, orderId: string) {
      const { dispatch } = _moduleActionContext(context);
      await setTemporaryOnOrderInOrderTable(orderId, false);
      await dispatch.getOrdersTable();
    },
    async removeOrder(context, param: { orderId: string; isFromExport: boolean }) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      rootDispatch.failIfRequestsInProgress();
      const isDeleteConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouReallyWantToRemoveOrder")),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "500px"
        )
      )) as boolean;
      if (isDeleteConfirmed) {
        let isDeleteAssignedBookings = false;
        if (param.isFromExport) {
          isDeleteAssignedBookings = (await rootDispatch.popup.addConfirm(
            new ConfirmModel(
              filterCapitalize(i18n.t("orderFromExportDoYouWantToCancelAssignedBookings")),
              filterCapitalize(i18n.t("warning")),
              [
                {
                  text: filterCapitalize(i18n.t("cancel")),
                  color: "warning",
                  value: false,
                  outlined: true,
                },
                {
                  text: filterCapitalize(i18n.t("confirm")),
                  color: "warning",
                  value: true,
                },
              ],
              "500px"
            )
          )) as boolean;
        }
        await removeOrder(state.selectedOrderId as string, isDeleteAssignedBookings);
        await dispatch.getOrdersTable();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    async removeOrderDocuments(context, param: { orderId: string }) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      rootDispatch.failIfRequestsInProgress();
      const isDeleteConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouReallyWantToRemoveDocumentsIssuedToThisOrder")),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "500px"
        )
      )) as boolean;
      if (isDeleteConfirmed) {
        const result = await removeOrderDocuments(param.orderId, false);

        if (result?.show) {
          const buttons = result.hasPermission
            ? [
                {
                  text: filterCapitalize(i18n.t("cancel")),
                  color: "warning",
                  value: false,
                  outlined: true,
                },
                {
                  text: filterCapitalize(i18n.t("confirm")),
                  color: "warning",
                  value: true,
                },
              ]
            : [
                {
                  text: filterCapitalize(i18n.t("close")),
                  color: "warning",
                  value: false,
                },
              ];

          const seccondConfirm = new ConfirmModel(
            result.message ?? "",
            filterCapitalize(filterCapitalize(i18n.t("warning"))),
            buttons
          );

          const isConfirmed = await rootDispatch.popup.addConfirm(seccondConfirm);

          if (isConfirmed) {
            await removeOrderDocuments(param.orderId, true);
            await rootDispatch.popup.addSavedOkSnackbar();
          }
        }

        await dispatch.getOrdersTable();
      }
    },
    async setErrorSendMailAsSended(context, orderId: string) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await setErrorSendAsSended(orderId);
      dispatch.getOrdersTable();
      rootDispatch.popup.addSavedOkSnackbar();
    },
    async getInformationAboutCorrectedItems(context, orderId: string) {
      await getInformationAboutCorrectedItems(orderId);
    },
    async disableAcceptationReminder(context, orderId: string) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await disableAcceptationReminder(orderId);
      dispatch.getOrdersTable();
      rootDispatch.popup.addSavedOkSnackbar();
    },
    async openIssueDateEdit(context, orderId: string) {
      const { commit } = _moduleActionContext(context);
      commit.setIssueDateOrderId(orderId);
      const issueDate = await getOrderIssueDate(orderId);
      commit.setIssueDate(issueDate);
    },
    async saveIssueDate(context) {
      const { dispatch, commit, state, rootDispatch } = _moduleActionContext(context);
      rootDispatch.failIfRequestsInProgress();
      await editOrderIssueDate(state.issueDateOrderId as string, state.issueDate as string);
      commit.setIssueDate(null);
      commit.setIssueDateOrderId(null);
      // noinspection ES6MissingAwait
      dispatch.getOrdersTable();
      rootDispatch.popup.addSavedOkSnackbar();
    },
    async createFastCorrection(context, params: OrderCreateCorrection) {
      const { dispatch, commit, state, rootDispatch } = _moduleActionContext(context);
      rootDispatch.failIfRequestsInProgress();
      await createCorrection(state.fastCorrectionOrderId as string, params);
      commit.setFastCorrectionOrderId(null);
      await dispatch.getOrdersTable();
      rootDispatch.popup.addSavedOkSnackbar();
    },
    async createDocumentFromOrderList(context, params: { orderId: string; timestamp: string }) {
      const { dispatch, state, rootDispatch } = _moduleActionContext(context);
      rootDispatch.failIfRequestsInProgress();
      const order = state.orders.find((v) => v.id === params.orderId);
      if (order !== undefined && order.hasReceipt && !order.isCorrection) {
        const confirmModel = ConfirmModel.confirmBoolFromContent(
          filterCapitalize(i18n.t("doYouReallyWantToIssueDocumentToReceipt"))
        );
        if (!(await rootDispatch.popup.addConfirm(confirmModel))) throw new Error("Action cancelled by user");
      }

      await dispatch.createDocument.issueDocument({
        orderId: params.orderId,
        timestamp: params.timestamp,
      });
    },
    async generateXmlDocument(context, documentId: string) {
      const { rootDispatch } = _moduleActionContext(context);
      rootDispatch.failIfRequestsInProgress();
      const tempFileId = await generateXmlDocument(documentId);
      await getOpenTemporaryFile(tempFileId, true);
    },
    async saveOderItemCashierOwner(context, cashierId: null | number) {
      const { rootDispatch, dispatch, state, commit } = _moduleActionContext(context);
      if (typeof state.selectedOrderItemId === "string") {
        const selectedOrderItemId = state.selectedOrderItemId;
        await setOrderItemCashierOwner(state.selectedOrderItemId, cashierId);
        rootDispatch.popup.addSavedOkSnackbar();
        await dispatch.getOrderItemsTable();
        commit.setSelectedOrderItemId(selectedOrderItemId);
      }
    },
    async openEditPurchaseNetPriceDialog(context, orderItemId: string) {
      const { commit } = _moduleActionContext(context);
      commit.setEditedPurchaseNetPriceOrderItemId(orderItemId);
      const orderItemPurchaseNetPrice = await getPurchaseNetPrice(orderItemId);
      commit.setOrderItemPurchaseNetPrice(orderItemPurchaseNetPrice);
      commit.setIsOrderItemPurchaseNetPriceDialog(true);
    },
    async openExportOrderDialog(context, order: OrderListItem) {
      const { dispatch, commit, rootDispatch } = _moduleActionContext(context);
      if (order.isExported) {
        const isConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("orderAlreadyExported")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("no")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("yes")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (!isConfirmed) return;
      }
      commit.setOrderExportSetting(new OrderExportSetting());
      commit.setSelectedOrderId(order.id);
      await dispatch.getExportSetting();
      commit.setIsExportOrderDialog(true);
    },
    async getExportSetting(context) {
      const { commit, state } = _moduleActionContext(context);
      const response = await getExportSettings(state.selectedOrderId as string);
      commit.setOrderExportSetting(response);
    },
    async exportOrder(context) {
      const { state, dispatch } = _moduleActionContext(context);
      await exportOrder(
        state.selectedOrderId as string,
        state.orderExportSetting.exportCostDocument,
        state.orderExportSetting.exportOrder,
        state.orderExportSetting.exportCostDocumentOther
      );
      await dispatch.getOrdersTable();
    },
    async createStandingOrder(context, standingOrder: StandingOrder) {
      const { state, commit, rootDispatch } = _moduleActionContext(context);
      if (state.standingOrderOrderId) {
        await createStandingOrder(state.standingOrderOrderId, standingOrder);
        commit.setStandingOrderOrderId(null);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
      // TODO - redirect to contractor standing order ????
    },
  },
});

export default OrdersModule;

/* eslint-disable  @typescript-eslint/no-explicit-any */
//eslint-disable-next-line
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, OrdersModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, OrdersModule);
